"use client";

import Link from "next/link";

import { useEffect, useMemo, useState } from "react";

import ProductCard from "@/components/card/ProductCard";

import AsianBasketFooter from "@/components/footer/AsianBasketFooter";

import { useCart, useCustomer } from "@/store/hooks";

import {
  ORDER_TYPE,
  isGroceryProduct,
  isTakeoutProduct,
} from "@/lib/orderType";

import {
  flattenBuyAgainItems,
  getOrderType,
  listCustomerOrdersDetailed,
} from "@/lib/accountService";

import { formatMoney } from "@/lib/format";

/* =========================================================
   HOME POPULAR TAG
   ========================================================= */

const HOME_POPULAR_TAG = "home-popular";

/* =========================================================
   PRODUCT TAG HELPER
   ========================================================= */

function hasProductTag(product, tagName) {
  const target = String(tagName || "")
    .trim()
    .toLowerCase();

  return (product?.tags || []).some((tag) => {
    const value = String(tag?.value || tag || "")
      .trim()
      .toLowerCase();

    return value === target;
  });
}

/* =========================================================
   FIRST AVAILABLE VARIANT
   ========================================================= */

function firstAvailableVariant(product) {
  const variants = product?.variants || [];

  return (
    variants.find(
      (variant) =>
        variant.manage_inventory === false ||
        variant.allow_backorder ||
        Number(variant.inventory_quantity ?? 1) > 0,
    ) || null
  );
}

/* =========================================================
   EXPAND PRODUCTS BY VARIANT

   Keeps your existing ProductCard behavior.
   ========================================================= */

function expandProductsByVariants(products = []) {
  return products.flatMap((product) => {
    const variants = product?.variants || [];

    if (!variants.length) {
      return [product];
    }

    return variants.map((variant) => ({
      ...product,

      /*
       * ProductCard receives exactly
       * one variant for this card.
       */

      variants: [variant],

      storefront_card_id: `${product.id}-${variant.id}`,
    }));
  });
}

/* =========================================================
   PRODUCT PRIMARY IMAGE
   ========================================================= */

function getProductPrimaryImage(product) {
  const variant = product?.variants?.[0];

  /* ===============================================
     VARIANT IMAGE
     =============================================== */

  if (variant?.images?.length) {
    const variantImage =
      typeof variant.images[0] === "string"
        ? variant.images[0]
        : variant.images[0]?.url;

    if (variantImage) {
      return variantImage;
    }
  }

  /* ===============================================
     PRODUCT THUMBNAIL
     =============================================== */

  if (product?.thumbnail) {
    return product.thumbnail;
  }

  /* ===============================================
     PRODUCT IMAGE
     =============================================== */

  if (product?.images?.length) {
    const productImage =
      typeof product.images[0] === "string"
        ? product.images[0]
        : product.images[0]?.url;

    if (productImage) {
      return productImage;
    }
  }

  return "/assets/images/product-placeholder.png";
}

/* =========================================================
   FIND CURRENT MEDUSA PRODUCT USING OLD ORDER VARIANT

   Important:
   Buy Again should render CURRENT product information,
   current price, current sale price and current stock.

   We do not render old order data as the product card.
   ========================================================= */

function findCurrentProductByVariant(products, variantId) {
  if (!variantId) {
    return null;
  }

  for (const product of products || []) {
    const variant = (product?.variants || []).find(
      (item) => item.id === variantId,
    );

    if (variant) {
      return {
        ...product,

        variants: [variant],

        storefront_card_id: `${product.id}-${variant.id}`,
      };
    }
  }

  return null;
}

/* =========================================================
   BUILD BUY AGAIN PRODUCTS

   1. Previous orders
   2. Unique variants
   3. Grocery only
   4. Map to CURRENT Medusa product
   ========================================================= */

function buildBuyAgainProducts(products, orders) {
  const previousItems = flattenBuyAgainItems(orders);

  const groceryItems = previousItems.filter(
    (item) => item.asian_basket_order_type === ORDER_TYPE.GROCERY,
  );

  return groceryItems
    .map((item) =>
      findCurrentProductByVariant(
        products,

        item.variant_id,
      ),
    )
    .filter(Boolean);
}

/* =========================================================
   VALID ORDER

   Don't treat cancelled orders as a purchase.
   ========================================================= */

function isUsableOrder(order) {
  const status = String(order?.status || "")
    .trim()
    .toLowerCase();

  return status !== "canceled" && status !== "cancelled";
}

/* =========================================================
   ORDER ITEM COUNT
   ========================================================= */

function getOrderItemCount(order) {
  return (order?.items || []).reduce(
    (total, item) => total + Number(item.quantity || 0),

    0,
  );
}

/* =========================================================
   ORDER DATE
   ========================================================= */

function formatOrderDate(value) {
  if (!value) {
    return "";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return new Intl.DateTimeFormat("en-CA", {
    month: "short",

    day: "numeric",

    year: "numeric",
  }).format(date);
}

/* =========================================================
   IMAGE FOR OLD ORDER ITEM
   ========================================================= */

function getOrderItemImage(item, products) {
  const variantId = item?.variant_id || item?.variant?.id;

  const currentProduct = findCurrentProductByVariant(products, variantId);

  if (currentProduct) {
    return getProductPrimaryImage(currentProduct);
  }

  if (item?.thumbnail) {
    return item.thumbnail;
  }

  return "/assets/images/product-placeholder.png";
}

/* =========================================================
   HOME STOREFRONT
   ========================================================= */

export default function HomeStorefront({ products = [] }) {
  /* =======================================================
     GROCERY CART
     ======================================================= */

  const { cart, addToCart, updateItemQuantity } = useCart(ORDER_TYPE.GROCERY);

  /* =======================================================
     CUSTOMER
     ======================================================= */

  const { customer, initialized: customerInitialized } = useCustomer();

  /* =======================================================
     ORDER STATE
     ======================================================= */

  const [customerOrders, setCustomerOrders] = useState([]);

  const [ordersLoading, setOrdersLoading] = useState(false);

  const [ordersError, setOrdersError] = useState("");

  /* =======================================================
     ADD ALL
     ======================================================= */

  const [addingAll, setAddingAll] = useState(false);

  const [addAllMessage, setAddAllMessage] = useState("");

  /* =======================================================
     LOAD CUSTOMER ORDERS

     Only authenticated customers.
     Guests never call /store/orders.
     ======================================================= */

  useEffect(() => {
    if (!customerInitialized) {
      return;
    }

    /* ===============================================
       GUEST
       =============================================== */

    if (!customer?.id) {
      setCustomerOrders([]);

      setOrdersLoading(false);

      setOrdersError("");

      return;
    }

    let cancelled = false;

    async function loadOrders() {
      setOrdersLoading(true);

      setOrdersError("");

      try {
        const result = await listCustomerOrdersDetailed({
          limit: 100,
        });

        if (cancelled) {
          return;
        }

        setCustomerOrders((result?.orders || []).filter(isUsableOrder));
      } catch (error) {
        if (cancelled) {
          return;
        }

        setCustomerOrders([]);

        setOrdersError(error?.message || "Unable to load previous orders.");
      } finally {
        if (!cancelled) {
          setOrdersLoading(false);
        }
      }
    }

    loadOrders();

    return () => {
      cancelled = true;
    };
  }, [customer?.id, customerInitialized]);

  /* =======================================================
     POPULAR GROCERY PRODUCTS

     Type:
     Grocery

     Tag:
     home-popular
     ======================================================= */

  const popularGrocery = useMemo(() => {
    const values = products.filter(
      (product) =>
        isGroceryProduct(product) && hasProductTag(product, HOME_POPULAR_TAG),
    );

    return expandProductsByVariants(values).slice(0, 6);
  }, [products]);

  /* =======================================================
     POPULAR TAKEOUT

     Type:
     Prepared Food

     Tag:
     home-popular
     ======================================================= */

  const popularFreshFood = useMemo(() => {
    const values = products.filter(
      (product) =>
        isTakeoutProduct(product) && hasProductTag(product, HOME_POPULAR_TAG),
    );

    return expandProductsByVariants(values).slice(0, 6);
  }, [products]);

  /* =======================================================
     BUY AGAIN

     Real previous Grocery purchases mapped back
     to current Medusa products.
     ======================================================= */

  const buyAgain = useMemo(
    () => buildBuyAgainProducts(products, customerOrders).slice(0, 6),

    [products, customerOrders],
  );

  /* =======================================================
     DOES CUSTOMER GET BUY AGAIN?
     ======================================================= */

  const hasBuyAgain = Boolean(
    customer?.id && !ordersLoading && buyAgain.length,
  );

  /* =======================================================
     HOME GROCERY SECTION

     Logged in + purchased Grocery:
     → Buy Again

     Otherwise:
     → Popular Grocery Products
     ======================================================= */

  const homeGroceryProducts = hasBuyAgain ? buyAgain : popularGrocery;

  /* =======================================================
     LATEST GROCERY ORDER
     ======================================================= */

  const lastGroceryOrder = useMemo(() => {
    return (
      [...customerOrders]
        .filter((order) => getOrderType(order) === ORDER_TYPE.GROCERY)
        .sort(
          (a, b) =>
            new Date(b?.created_at || 0).getTime() -
            new Date(a?.created_at || 0).getTime(),
        )[0] || null
    );
  }, [customerOrders]);

  /* =======================================================
     LAST ORDER THUMBNAILS
     ======================================================= */

  const lastOrderThumbnails = useMemo(() => {
    if (!lastGroceryOrder) {
      return [];
    }

    return (lastGroceryOrder?.items || []).slice(0, 5).map((item, index) => ({
      key: item?.id || item?.variant_id || index,

      image: getOrderItemImage(item, products),

      title: item?.product_title || item?.title || "Product",
    }));
  }, [lastGroceryOrder, products]);

  /* =======================================================
     LAST ORDER COUNT
     ======================================================= */

  const lastOrderItemCount = lastGroceryOrder
    ? getOrderItemCount(lastGroceryOrder)
    : 0;

  const lastOrderRemaining = Math.max(
    lastOrderItemCount - lastOrderThumbnails.length,

    0,
  );

  /* =======================================================
     SECTION COPY / LINKS
     ======================================================= */

  const grocerySectionTitle =
    ordersLoading && customer?.id
      ? "Buy Again"
      : hasBuyAgain
        ? "Buy Again"
        : "Popular Grocery Picks";

  const grocerySectionDescription = hasBuyAgain
    ? "Your previously purchased grocery favourites."
    : "Popular grocery favourites from Asian Basket.";

  const grocerySectionLink = hasBuyAgain
    ? "/account/buy-again"
    : "/shop/groceries";

  const grocerySectionLinkLabel = hasBuyAgain ? "View all" : "View groceries";

  /* =======================================================
     ADD ALL GROCERY SECTION ITEMS
     ======================================================= */

  async function addAllHomeGrocery() {
    if (!homeGroceryProducts.length || addingAll) {
      return;
    }

    setAddingAll(true);

    setAddAllMessage("");

    try {
      for (const product of homeGroceryProducts.slice(0, 5)) {
        const variant = firstAvailableVariant(product);

        /*
         * Out of stock products are skipped.
         */

        if (!variant?.id) {
          continue;
        }

        const line = (cart?.items || []).find(
          (item) =>
            item.variant_id === variant.id || item.variant?.id === variant.id,
        );

        if (line) {
          await updateItemQuantity(
            line.id,

            Number(line.quantity || 0) + 1,
          );
        } else {
          await addToCart(variant.id, 1);
        }
      }

      setAddAllMessage("Added to basket");

      window.setTimeout(
        () => setAddAllMessage(""),

        2200,
      );
    } catch (error) {
      setAddAllMessage(error?.message || "Unable to add these items.");
    } finally {
      setAddingAll(false);
    }
  }

  return (
    <main className="asian-bucket-home-page-main-wrapper">
      <div className="custom-container">
        {/* =================================================
            HERO
            ================================================= */}

        <section className="asian-bucket-hero-section-main-wrapper">
          {/* Grocery Hero */}

          <article
            className="asian-bucket-hero-card asian-bucket-grocery-hero-card"
            style={{
              "--asian-bucket-hero-bg":
                "url('/assets/images/hero-grocery-background.png')",
            }}
          >
            <div className="asian-bucket-hero-content-main-wrapper">
              <div className="asian-bucket-hero-copy-main-wrapper">
                <h2>Fresh Ingredients Brighter Meals Happier Homes</h2>

                <p>Authentic Indian groceries for your family, every day.</p>

                <Link
                  href="/shop/groceries"
                  className="asian-bucket-hero-primary-button asian-bucket-green-button"
                >
                  <span>Shop Groceries</span>

                  <img src="/assets/icons/arrow-right.svg" alt="" />
                </Link>
              </div>

              <div className="asian-bucket-grocery-features-main-wrapper">
                <div className="asian-bucket-hero-feature-item">
                  <img src="/assets/icons/leaf.svg" alt="" />

                  <span>Fresh Produce</span>
                </div>

                <span className="asian-bucket-hero-feature-divider" />

                <div className="asian-bucket-hero-feature-item">
                  <img src="/assets/icons/shield.svg" alt="" />

                  <span>Trusted Brands</span>
                </div>

                <span className="asian-bucket-hero-feature-divider" />

                <div className="asian-bucket-hero-feature-item">
                  <img src="/assets/icons/truck.svg" alt="" />

                  <span>Everyday Essentials</span>
                </div>
              </div>
            </div>
          </article>

          {/* Takeout Hero */}

          <article
            className="asian-bucket-hero-card asian-bucket-takeout-hero-card"
            style={{
              "--asian-bucket-hero-bg":
                "url('/assets/images/hero-takeout-background.png')",
            }}
          >
            <div className="asian-bucket-hero-content-main-wrapper">
              <div className="asian-bucket-hero-copy-main-wrapper asian-bucket-takeout-copy-main-wrapper">
                <h2>
                  Vegetarian <br />
                  Takeout Made with Love
                </h2>

                <p>Bold flavors. Freshly prepared. Truly satisfying.</p>

                <Link
                  href="/shop/take-out"
                  className="asian-bucket-hero-primary-button asian-bucket-orange-button"
                >
                  <span>Order Takeout</span>

                  <img src="/assets/icons/arrow-right.svg" alt="" />
                </Link>
              </div>

              <div className="asian-bucket-takeout-note-main-wrapper">
                <span>Same</span>

                <span>Great Taste</span>

                <span>Closer to Home</span>

                <strong>♡</strong>
              </div>

              <div className="asian-bucket-takeout-footer-main-wrapper">
                <div className="asian-bucket-takeout-features-main-wrapper">
                  <div className="asian-bucket-hero-feature-item">
                    <img src="/assets/icons/leaf.svg" alt="" />

                    <span>100% Vegetarian</span>
                  </div>

                  <span className="asian-bucket-takeout-feature-divider" />

                  <div className="asian-bucket-hero-feature-item">
                    <img src="/assets/icons/chef.svg" alt="" />

                    <span>Freshly Made</span>
                  </div>

                  <span className="asian-bucket-takeout-feature-divider" />

                  <div className="asian-bucket-hero-feature-item">
                    <img src="/assets/icons/heart.svg" alt="" />

                    <span>Authentic Indian Taste</span>
                  </div>
                </div>

                <div className="asian-bucket-provider-icons-main-wrapper">
                  <img src="/assets/icons/ubereats.svg" alt="Uber Eats" />

                  <img src="/assets/icons/doordash.svg" alt="DoorDash" />
                </div>
              </div>
            </div>
          </article>
        </section>

        {/* =================================================
            COMMUNITY
            ================================================= */}

        {/* <section className="asian-bucket-community-section-main-wrapper">
          <div className="asian-bucket-community-image-main-wrapper">
            <img
              src="/assets/images/community-store.png"
              alt="Asian Basket storefront"
            />
          </div>

          <div className="asian-bucket-community-content-main-wrapper">
            <div className="asian-bucket-community-top-row">
              <span className="asian-bucket-community-badge">
                <img src="/assets/icons/users.svg" alt="" />A COMMUNITY
                FAVOURITE IN WINDSOR/ESSEX
              </span>

              <a href="#" className="asian-bucket-green-pill-link">
                Read Google Reviews{" "}
                <img src="/assets/icons/arrow-right.svg" alt="" />
              </a>
            </div>

            <h2>Trusted by Local Families</h2>

            <p>
              Quality products, great service and a stronger community –
              that&apos;s Asian Basket.
            </p>

            <div className="asian-bucket-community-detail-grid">
              <div className="asian-bucket-rating-main-wrapper">
                <div className="asian-bucket-google-rating">
                  <b>G</b>

                  <strong>4.8</strong>

                  <span>★★★★★</span>

                  <small>(500+ reviews)</small>
                </div>

                <div className="asian-bucket-review-row">
                  <blockquote>
                    “Best Indian store in Brampton! Great selection and friendly
                    staff.”
                    <b>– Aman S.</b>
                  </blockquote>

                  <blockquote>
                    “Fresh produce, authentic products and amazing prices!”
                    <b>– Priya K.</b>
                  </blockquote>
                </div>
              </div>

              <div className="asian-bucket-community-benefits-main-wrapper">
                <div>
                  <img src="/assets/icons/truck.svg" alt="" />

                  <p>
                    <b>500+</b>

                    <small>Google Reviews</small>
                  </p>
                </div>

                <div>
                  <img src="/assets/icons/truck.svg" alt="" />

                  <p>
                    <b>Pickup &amp;</b>

                    <small>Local Delivery</small>
                  </p>
                </div>

                <div>
                  <img src="/assets/icons/leaf.svg" alt="" />

                  <p>
                    <b>Fresh</b>

                    <small>Produce Always</small>
                  </p>
                </div>

                <div>
                  <img src="/assets/icons/users.svg" alt="" />

                  <p>
                    <b>Serving Windsor,</b>

                    <small>Essex &amp; Surrounding Communities</small>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section> */}

        <section className="asian-bucket-community-section-main-wrapper">
          {/* ================================
      LEFT - STORE IMAGE
      ================================ */}
          <div className="asian-bucket-community-image-main-wrapper">
            <img
              src="/assets/images/community-store.png"
              alt="Asian Basket Indian grocery store in Windsor and Essex"
            />
          </div>

          {/* ================================
      RIGHT - CONTENT
      ================================ */}
          <div className="asian-bucket-community-content-main-wrapper">
            {/* HEADING */}
            <div className="asian-bucket-community-heading-main-wrapper">
              <h2>Your Local Indian Grocery Store</h2>

              <h3>Serving Windsor &amp; Essex</h3>

              <p>
                Asian Basket brings Indian groceries, fresh food and vegetarian
                takeout together in one local store. Shop in-store, order for
                pickup, or have groceries delivered throughout the Windsor–Essex
                area.
              </p>
            </div>

            {/* ================================
        GOOGLE REVIEWS
        ================================ */}
            <div className="asian-bucket-community-google-row">
              <div className="asian-bucket-community-google-rating">
                <span className="asian-bucket-google-icon">
                  <img src="/assets/icons/google-logo.jpeg" alt="google logo" />
                </span>

                <strong>4.8</strong>

                <span className="asian-bucket-community-stars">★★★★★</span>

                <small>(520+ reviews)</small>
              </div>

              <span className="asian-bucket-community-review-divider"></span>

              <a href="#" className="asian-bucket-community-google-review-link">
                <span>Read Google Reviews</span>

                <span className="asian-bucket-community-review-arrow">→</span>
              </a>
            </div>

            {/* ================================
        BENEFITS
        ================================ */}
            <div className="asian-bucket-community-benefits-row">
              {/* FRESH GROCERIES */}
              <div className="asian-bucket-community-benefit-item">
                <div className="asian-bucket-community-benefit-icon">
                  <svg
                    viewBox="0 0 48 48"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path
                      d="M8 18H40L37 40H11L8 18Z"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinejoin="round"
                    />

                    <path
                      d="M16 18L22 8"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                    />

                    <path
                      d="M32 18L26 8"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                    />

                    <path
                      d="M18 25V33"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                    />

                    <path
                      d="M30 25V33"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>

                <div className="asian-bucket-community-benefit-content">
                  <strong>Fresh Groceries</strong>

                  <p>
                    Wide selection of
                    <br />
                    Indian products
                  </p>
                </div>
              </div>

              {/* VEGETARIAN TAKEOUT */}
              <div className="asian-bucket-community-benefit-item">
                <div className="asian-bucket-community-benefit-icon is-orange">
                  <svg
                    viewBox="0 0 48 48"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path
                      d="M9 32H39"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                    />

                    <path
                      d="M12 32C12 24.8 17.4 19 24 19C30.6 19 36 24.8 36 32"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                    />

                    <path
                      d="M8 36H40"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                    />

                    <path
                      d="M17 14C14.5 11.5 18.5 9.5 16 7"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                    />

                    <path
                      d="M24 14C21.5 11.5 25.5 9.5 23 7"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                    />

                    <path
                      d="M31 14C28.5 11.5 32.5 9.5 30 7"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>

                <div className="asian-bucket-community-benefit-content">
                  <strong>Vegetarian Takeout</strong>

                  <p>
                    Freshly prepared
                    <br />
                    every day
                  </p>
                </div>
              </div>

              {/* PICKUP & DELIVERY */}
              <div className="asian-bucket-community-benefit-item">
                <div className="asian-bucket-community-benefit-icon">
                  <svg
                    viewBox="0 0 52 48"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path
                      d="M5 12H31V34H5V12Z"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinejoin="round"
                    />

                    <path
                      d="M31 20H40L47 27V34H31V20Z"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinejoin="round"
                    />

                    <circle
                      cx="15"
                      cy="36"
                      r="4"
                      stroke="currentColor"
                      strokeWidth="2.5"
                    />

                    <circle
                      cx="39"
                      cy="36"
                      r="4"
                      stroke="currentColor"
                      strokeWidth="2.5"
                    />
                  </svg>
                </div>

                <div className="asian-bucket-community-benefit-content">
                  <strong>Pickup &amp; Local Delivery</strong>

                  <p>
                    Convenient options
                    <br />
                    across Windsor–Essex
                  </p>
                </div>
              </div>
            </div>

            {/* ================================
        CTA
        ================================ */}
            <div className="asian-bucket-community-cta-main-wrapper">
              <a href="#" className="asian-bucket-community-visit-store-button">
                <span>Visit Our Store</span>

                <span>→</span>
              </a>
            </div>
          </div>
        </section>

        {/* =================================================
            BUY AGAIN / POPULAR GROCERY

            SAME EXISTING CLASSES.
            ================================================= */}

        <section className="asian-bucket-buy-again-section-main-wrapper">
          <div className="asian-bucket-section-heading-main-wrapper">
            <div className="asian-bucket-section-heading-copy">
              <span className="asian-bucket-round-section-icon">
                <img src="/assets/icons/basket.svg" alt="" />
              </span>

              <div>
                <h2>{grocerySectionTitle}</h2>

                <p>{grocerySectionDescription}</p>
              </div>
            </div>

            <Link href={grocerySectionLink}>
              {grocerySectionLinkLabel}{" "}
              <img src="/assets/icons/arrow-right.svg" alt="" />
            </Link>
          </div>

          <div className="asian-bucket-buy-again-layout-main-wrapper">
            <div className="asian-bucket-home-product-grid asian-bucket-food-product-grid">
              {/* ===========================================
                  LOGGED-IN ORDER LOADING
                  =========================================== */}

              {customer?.id && ordersLoading ? (
                <p className="asian-bucket-empty-home-message">
                  Loading your previous purchases...
                </p>
              ) : homeGroceryProducts.length ? (
                homeGroceryProducts
                  .slice(0, 5)
                  .map((product) => (
                    <ProductCard
                      key={product.storefront_card_id || product.id}
                      product={product}
                      compact
                    />
                  ))
              ) : (
                <p className="asian-bucket-empty-home-message">
                  Add the home-popular tag to Grocery products in Medusa and
                  they will appear here.
                </p>
              )}
            </div>

            {/* =================================================
                REAL LAST ORDER
                ================================================= */}

            {hasBuyAgain && lastGroceryOrder ? (
              <aside className="asian-bucket-last-order-main-wrapper">
                <div className="asian-bucket-last-order-heading">
                  <img src="/assets/icons/clock.svg" alt="" />

                  <div>
                    <h3>Your Last Order</h3>

                    <p>
                      {formatOrderDate(lastGroceryOrder.created_at)}
                      {" · "}
                      {lastOrderItemCount}{" "}
                      {lastOrderItemCount === 1 ? "item" : "items"}
                      {" · "}
                      {formatMoney(
                        lastGroceryOrder.total,
                        lastGroceryOrder.currency_code || "cad",
                      )}
                    </p>
                  </div>
                </div>

                <div className="asian-bucket-last-order-thumbnails">
                  {lastOrderThumbnails.map((item) => (
                    <img key={item.key} src={item.image} alt={item.title} />
                  ))}

                  {lastOrderRemaining > 0 ? (
                    <span>+{lastOrderRemaining}</span>
                  ) : null}
                </div>

                <button
                  type="button"
                  className="asian-bucket-last-order-add-button"
                  onClick={addAllHomeGrocery}
                  disabled={!homeGroceryProducts.length || addingAll}
                >
                  <img src="/assets/icons/basket.svg" alt="" />

                  {addingAll ? "Adding..." : "Add All to Cart"}

                  <img src="/assets/icons/arrow-right.svg" alt="" />
                </button>

                <Link
                  href={`/account/orders/${lastGroceryOrder.id}`}
                  className="asian-bucket-last-order-view-button"
                >
                  View Order
                </Link>

                <p className="asian-bucket-last-order-note">
                  <img src="/assets/icons/leaf.svg" alt="" />
                  Reorder your regulars in one tap.
                </p>

                {addAllMessage && (
                  <small className="asian-bucket-last-order-message">
                    {addAllMessage}
                  </small>
                )}
              </aside>
            ) : (
              /* =================================================
                 NO PREVIOUS GROCERY PURCHASE

                 Keep SAME sidebar class/design, but make it
                 useful instead of showing fake order data.
                 ================================================= */

              <aside className="asian-bucket-last-order-main-wrapper">
                <div className="asian-bucket-last-order-heading">
                  <img src="/assets/icons/basket.svg" alt="" />

                  <div>
                    <h3>Popular Grocery Picks</h3>

                    <p>
                      {popularGrocery.length
                        ? `${popularGrocery.length} popular items selected from Medusa`
                        : "Add home-popular tags in Medusa"}
                    </p>
                  </div>
                </div>

                <div className="asian-bucket-last-order-thumbnails">
                  {popularGrocery.slice(0, 5).map((product) => {
                    const image = getProductPrimaryImage(product);

                    return (
                      <img
                        key={product.storefront_card_id || product.id}
                        src={image}
                        alt={product.title}
                      />
                    );
                  })}

                  {popularGrocery.length > 5 ? (
                    <span>+{popularGrocery.length - 5}</span>
                  ) : null}
                </div>

                <button
                  type="button"
                  className="asian-bucket-last-order-add-button"
                  onClick={addAllHomeGrocery}
                  disabled={
                    !popularGrocery.length || addingAll || ordersLoading
                  }
                >
                  <img src="/assets/icons/basket.svg" alt="" />

                  {addingAll ? "Adding..." : "Add Popular Picks"}

                  <img src="/assets/icons/arrow-right.svg" alt="" />
                </button>

                <Link
                  href="/shop/groceries"
                  className="asian-bucket-last-order-view-button"
                >
                  Shop Groceries
                </Link>

                <p className="asian-bucket-last-order-note">
                  <img src="/assets/icons/leaf.svg" alt="" />
                  Popular grocery favourites from Asian Basket.
                </p>

                {ordersError ? (
                  <small className="asian-bucket-last-order-message">
                    Previous orders could not be loaded. Showing popular
                    products instead.
                  </small>
                ) : null}

                {addAllMessage && (
                  <small className="asian-bucket-last-order-message">
                    {addAllMessage}
                  </small>
                )}
              </aside>
            )}
          </div>
        </section>

        {/* =================================================
            POPULAR FRESH FOOD

            ONLY:
            Type = Prepared Food
            Tag = home-popular
            ================================================= */}

        <section className="asian-bucket-popular-food-section-main-wrapper">
          <div className="asian-bucket-section-heading-main-wrapper">
            <div className="asian-bucket-section-heading-copy">
              <span className="asian-bucket-round-section-icon asian-bucket-orange-icon">
                <img src="/assets/icons/bowl.svg" alt="" />
              </span>

              <div>
                <h2>Popular Fresh Food</h2>

                <p>Customer favourites, made in our kitchen.</p>
              </div>
            </div>

            <Link href="/shop/take-out">
              View full menu <img src="/assets/icons/arrow-right.svg" alt="" />
            </Link>
          </div>

          <div className="asian-bucket-popular-food-layout-main-wrapper">
            <div className="asian-bucket-home-product-grid asian-bucket-food-product-grid">
              {popularFreshFood.length ? (
                popularFreshFood
                  .slice(0, 5)
                  .map((product) => (
                    <ProductCard
                      key={product.storefront_card_id || product.id}
                      product={product}
                      compact
                    />
                  ))
              ) : (
                <p className="asian-bucket-empty-home-message">
                  Add the home-popular tag to Prepared Food products in Medusa
                  and they will appear here.
                </p>
              )}
            </div>

            <aside className="asian-bucket-meal-pickup-main-wrapper">
              <h3>
                Add a Meal to
                <br />
                Your Grocery
                <br />
                Pickup
              </h3>

              <p>
                Save time — we&apos;ll have
                <br />
                it ready together.
              </p>

              <Link href="/shop/take-out">
                Explore Meals
                <img src="/assets/icons/arrow-right.svg" alt="" />
              </Link>
            </aside>
          </div>
        </section>

        {/* =================================================
            PROMOTIONS
            ================================================= */}

        <section className="asian-bucket-promotions-section-main-wrapper">
          <article className="asian-bucket-promo-card asian-bucket-weekly-promo-card">
            <div>
              <h2>Weekly Specials</h2>

              <p>Big savings on your favourite brands.</p>

              <Link href="/weekly-specials">Shop Deals</Link>
            </div>
          </article>

          <article className="asian-bucket-promo-card asian-bucket-festival-promo-card">
            <div>
              <h2>Festival Essentials</h2>

              <p>
                Everything you need for
                <br />
                Diwali, Vaisakhi and more.
              </p>

              <Link href="/shop/pooja">Shop Now</Link>
            </div>
          </article>
        </section>

        <AsianBasketFooter />
      </div>
    </main>
  );
}
